/**
 * Which aspects/properties a `FilterGroup` reads — used by the Chord writer
 * (`buildTransitionChord.ts`) to decide which aspects a calculated transition
 * snapshots. Only plain property leaves (`type: 'property'`, the default) name
 * a single aspect/property; the other `FilterExpressionType`s (`children_count`,
 * `elapsed_in_stage`, `approaches_deployed`, `arc_status`, `custom`) evaluate a
 * derived fact instead and are recorded as `ConditionFact`s, not aspect deltas.
 * Lane leaves (perspective/relationship/arc) reference spheres, not aspects.
 */
import { FilterGroup, FilterGroupLeaf } from 'types/filters';
import { isPropertyLeaf } from './filterGroupLeaf';

function leafReferencedAspect(leaf: FilterGroupLeaf): string | null {
  if (!isPropertyLeaf(leaf)) return null;
  if (leaf.type !== undefined && leaf.type !== 'property') return null;
  const name = leaf.aspect ?? leaf.property;
  return name || null;
}

export function referencedAspects(filterGroup: FilterGroup): string[] {
  const seen = new Set<string>();
  const walk = (group: FilterGroup): void => {
    // `?? []` — a group with no `expressions` is legitimately empty (and, before
    // `normalizeFilterGroup`, was the persisted legacy `conditions[]` shape). Neither
    // may throw: this walk runs on every calculated transition.
    for (const leaf of group.expressions ?? []) {
      const name = leafReferencedAspect(leaf);
      if (name) seen.add(name);
    }
    for (const nested of group.groups ?? []) walk(nested);
  };
  walk(filterGroup);
  return [...seen];
}
